import axios from "axios";
import {
  SIGNATURE_BASE_URL,
  SIGNATURE_DASH_URL,
  FIREBASE_CONFIG_SIGNATURE,
} from "@constants/constants";
import FB from "@utils/tracking/facebook";
function getUTMDataLocal() {
  try {
    if (typeof window !== "undefined") {
      return JSON.parse(localStorage.getItem("utmData") || "{}");
    }
    return {};
  } catch (error) {
    console.log("error in getUTMDataLocal");
    console.log(error);
    return {};
  }
}

/**
 * Creates a UTM query string from the provided UTM data or from local storage.
 * If no UTM data is provided, it attempts to retrieve it from local storage.
 * Ensures that the 'utm_source' is set to the provided 'from' value if not already present.
 * Returns the query string prefixed with the specified joiner (default is "&").
 *
 * @param {Object} params - The parameters for creating the UTM query string.
 * @param {Object} params.utmData_ - The UTM data object.
 * @param {string} params.from - The source value to set if 'utm_source' is not present in UTM data.
 * @param {string} [params.joiner="&"] - The joiner to prefix the query string with.
 * @returns {string} The generated UTM query string.
 */
function createUTMQueryParams({ utmData_, from, joiner = "&" }) {
  try {
    // Initialize utmData with the provided utmData_ or an empty object if not provided
    let utmData = utmData_ || {};

    // Check if utmData has any keys (parameters)
    let utmHasParams = Object.keys(utmData)?.length;

    // If utmData does not have any parameters, get UTM data from local storage
    if (!utmHasParams) utmData = getUTMDataLocal();

    // If utmData does not have a utm_source, set it to the provided 'from' value
    if (!utmData?.utm_source && from) utmData.utm_source = from;

    // Recheck if utmData has any keys (parameters) after attempting to get from local storage
    utmHasParams = Object.keys(utmData)?.length;

    // If utmData still does not have any parameters, return an empty string
    if (!utmHasParams) return "";

    // Create a query string from the utmData object
    let params = `${Object.entries(utmData)
      .map(([key, value]) => `${key}=${value}`)
      .join("&")}`;

    // Return the query string prefixed with the joiner (default is "&")
    return `${joiner}${params}`;
  } catch (error) {
    // Log any errors that occur during the process
    console.log("error in createUTMQueryParams");
    console.log(error);
    return "";
  }
}

function getAccessTokenLoginURL(result) {
  const user = result.user;
  const fields = ["accessToken", "email", "name", "photoURL", "phoneNumber"];
  const from = localStorage.getItem("from");

  let url = `${SIGNATURE_DASH_URL}/login?`;
  for (let field of fields) {
    url += `${field}=${user[field]}&`;
  }
  if (from) {
    url += `&from=${from}`;
  }
  return url;
}

async function get2FAInfo({ email }) {
  try {
    const { data } = await axios.post(`${SIGNATURE_BASE_URL}/2fa/get-info`, {
      email,
    });
    return {
      verified: data?.verified || false,
      enabled: data?.enabled || false,
    };
  } catch (error) {
    console.log("Error in get2FAInfo");
    console.log(error);
    return { verified: false, enabled: false };
  }
}

async function updateLoginInfoOnlyBackend(result, method) {
  console.log("updateLoginInfoOnlyBackend", result, method);
  let utmData = JSON.parse(localStorage.getItem("utmData") || "{}");
  const user = result.user;
  user.email = user?.email?.toLowerCase();
  let timezone = Intl?.DateTimeFormat()?.resolvedOptions()?.timeZone;
  const fromParam = localStorage.getItem("from");

  try {
    let { data } = await axios.post(`${SIGNATURE_BASE_URL}/owners`, {
      email: user.email,
      name: user.displayName,
      profilePic: user.photoURL,
      phone: user.phoneNumber,
      from: fromParam,
      timezone,
      accessToken: result?.user?.accessToken,
      projectId: FIREBASE_CONFIG_SIGNATURE?.projectId,
      utmData,
    });
    console.log("data", data);
    // posthogIdentifyWrapper({ owner: data?.owner });
  } catch (error) {
    console.log(error);
  }
}

async function updateLoginInfo(result, method) {
  console.log("updateLoginInfo", result, method);
  const utmData = JSON.parse(localStorage.getItem("utmData") || "{}");
  const user = result.user;
  user.email = user?.email?.toLowerCase();
  let timezone = Intl?.DateTimeFormat()?.resolvedOptions()?.timeZone;
  const fromParam = localStorage.getItem("from");
  try {
    let { data } = await axios.post(`${SIGNATURE_BASE_URL}/owners`, {
      email: user.email,
      name: user.displayName,
      profilePic: user.photoURL,
      phone: user.phoneNumber,
      from: fromParam,
      timezone,
      accessToken: result?.user?.accessToken,
      projectId: FIREBASE_CONFIG_SIGNATURE?.projectId,
      utmData,
    });
    if (data?.owner?.isSignUp) {
      new FB().trackSignUp(user.email);
    }
    if (data?.owner?.email && user.email === data?.owner?.email) {
      window.open(getAccessTokenLoginURL(result), "_self");
      return { message: "Redirecting to dashboard" };
    }
  } catch (error) {
    console.log(error);
    return { error: "Something went wrong" };
  }
}

const handleVerify = async ({ email, OTP, userData, cb }) => {
  try {
    let url = `${SIGNATURE_BASE_URL}/2fa/verify-qr`;
    const body = { otp: OTP, email };
    const { data } = await axios.post(url, body);
    console.log(data);
    if (data?.success && data?.isValid) {
      await updateLoginInfo(userData?.result, userData?.method);
      cb?.({ success: true });
      return { success: true };
    } else {
      cb?.({ success: false });
      return { success: false, error: "Invalid OTP" };
    }
  } catch (error) {
    console.log("error in handleVerify");
    console.log(error);
    return { success: false, error: "Something went wrong" };
  }
};

const is1DollarTrialCampaign = ({ utm_campaign }) => {
  const SHOW_1$_TRIAL_CAMPAIGN = ["10_dollar_campaign", "paidtrial"];
  let utmData = getUTMDataLocal();
  let utm_campaign_ = utm_campaign || utmData?.utm_campaign;
  if (!utm_campaign_) return false;
  return SHOW_1$_TRIAL_CAMPAIGN.includes(utm_campaign_);
};

function createCheckoutURL({ priceId, quantity, value, product, cycle }) {
  let calculatedValue = cycle === "annually" ? value * 12 : value;
  calculatedValue *= quantity;
  if (isNaN(calculatedValue)) calculatedValue = value;
  return `${SIGNATURE_DASH_URL}/checkout?priceId=${priceId}&quantity=${quantity}&value=${calculatedValue}&product=${product}`;
}

function createRegisterURL({ from, utmData }) {
  return `${SIGNATURE_DASH_URL}/register?from=${from}${createUTMQueryParams({
    from,
    utmData_: utmData,
  })}`;
}

export {
  handleVerify,
  get2FAInfo,
  updateLoginInfoOnlyBackend,
  updateLoginInfo,
  getAccessTokenLoginURL,
  getUTMDataLocal,
  createUTMQueryParams,
  is1DollarTrialCampaign,
  createCheckoutURL,
  createRegisterURL,
};
